import { Box, Link, Stack } from "@mui/material";
import { BoxProps } from "@mui/material/Box";
import { IExtendedSearchItem } from "src/api/client/extended-search.api.types";
import { CardRankings } from "src/components/pages/search/components/card-result/components/card-rankings";
import { useSearchStore } from "src/components/pages/search/store/search-store";
import { Typography } from "src/components-mui/base/typography/typography";
import { GoToWikifolioButton } from "src/components-mui/common/buttons/go-to-wikifolio-button/go-to-wikifolio-button";
import { WatchlistButton } from "src/components-mui/common/buttons/watchlist-button/watchlist-button";
import { TraderAvatar } from "src/components-mui/common/trader-avatar/trader-avatar";
import { mapTraderToAvatarUser } from "src/components-mui/common/trader-avatar/trader-avatar.mappers";
import { WikifolioOgChartImage } from "src/components-mui/common/wikifolio-og-chart-image/wikifolio-og-chart-image";
import { useGetUserName } from "src/hooks/use-get-user-name";
import { useLinkBuilder } from "src/hooks/use-link-builder";
import { getTrackingUrlParam } from "src/utils/search-tracking-util";

const SEARCH_RESULT_CARD_TEST_ID = "search-result-card";
const SEARCH_RESULT_CARD_WIKIFOLIO_LINK_TEST_ID = "search-result-card-wikifolio-link";
const SEARCH_RESULT_CARD_TRADER_PROFILE_LINK_TEST_ID = "search-result-card-trader-profile-link";
const SEARCH_RESULT_CARD_CHART_IMAGE_LINK_TEST_ID = "search-result-card-chart-image-link";

const placement = "extended";

export interface ISearchResultCardProps extends IExtendedSearchItem, BoxProps {
    currency?: string;
}

export const SearchResultCard = ({
    wikifolioId,
    symbol,
    wikifolioShortDescription,
    trader,
    figures,
    currency,
    isOnWatchlist: isOnWatchlistFromServer = false,

    ...boxProps
}: ISearchResultCardProps) => {
    const { buildTraderProfileLink, buildWikifolioLink } = useLinkBuilder();
    const { query } = useSearchStore();
    const getUserName = useGetUserName();

    const traderProfileLink = buildTraderProfileLink(trader.nickName);
    const wikifolioLink = buildWikifolioLink(symbol, getTrackingUrlParam({ placement, query }));
    const headingId = `${SEARCH_RESULT_CARD_TEST_ID}-heading-${wikifolioId}`;

    return (
        <Box
            component="article"
            aria-labelledby={headingId}
            data-test-id={SEARCH_RESULT_CARD_TEST_ID}
            {...boxProps}
            sx={[
                {
                    height: "max-content",
                    p: [2, 3],
                    flex: 1,
                    flexDirection: "column",
                    minWidth: 0,
                    borderRadius: "8px",
                    boxShadow: 3,
                },
                ...(Array.isArray(boxProps.sx) ? boxProps.sx : [boxProps.sx]),
            ]}
        >
            <Stack component="header" direction="row" sx={{ mb: 2 }}>
                <TraderAvatar
                    sx={{ flexShrink: 0, mr: 1 }}
                    size="md"
                    trader={mapTraderToAvatarUser({ trader })}
                    href={traderProfileLink}
                    target="_blank"
                    loading="eager"
                />
                <Stack direction="column" sx={{ flexGrow: 1, overflow: "hidden" }}>
                    <Typography id={headingId} component="h3">
                        <Link
                            href={wikifolioLink}
                            target="_blank"
                            underline="hover"
                            noWrap
                            sx={{ fontSize: "md", display: "block" }}
                            data-test-id={SEARCH_RESULT_CARD_WIKIFOLIO_LINK_TEST_ID}
                        >
                            {wikifolioShortDescription}
                        </Link>
                    </Typography>
                    <Link
                        href={traderProfileLink}
                        target="_blank"
                        underline="hover"
                        noWrap
                        sx={{ fontSize: "sm", display: "block", fontWeight: "normal" }}
                        data-test-id={SEARCH_RESULT_CARD_TRADER_PROFILE_LINK_TEST_ID}
                    >
                        {getUserName(trader)}
                    </Link>
                </Stack>
            </Stack>
            <Link
                href={wikifolioLink}
                data-test-id={SEARCH_RESULT_CARD_CHART_IMAGE_LINK_TEST_ID}
                target="_blank"
                underline="none"
                aria-hidden
                tabIndex={-1}
                sx={{ display: "block", mb: 2, mx: [-2, -3] }}
            >
                <WikifolioOgChartImage loading="lazy" ratio={21 / 9} wikifolioId={wikifolioId} shortDescription={wikifolioShortDescription} />
            </Link>
            <CardRankings figures={figures} currency={currency} mb={2} />
            <Stack component="footer" direction="row">
                <GoToWikifolioButton sx={{ flex: 1 }} variant="outlined" href={wikifolioLink} target="_blank" size="small" />
                <WatchlistButton
                    size="small"
                    sx={{ ml: 2, p: 0 }}
                    omitTitle
                    wikifolioShortDescription={wikifolioShortDescription}
                    wikifolioId={wikifolioId}
                    isOnWatchlist={isOnWatchlistFromServer}
                />
            </Stack>
        </Box>
    );
};
